import * as yup from "yup";
import "yup-phone-lite";
import { timeWithin30Min } from "../../utils/utils";

// Check if password contains 3+ consecutive letters or numbers
function hasInvalidSequence(password) {
  const lowerPass = password.toLowerCase();
  for (let i = 0; i < lowerPass.length - 2; i++) {
    const first = lowerPass[i];
    const second = lowerPass[i + 1];
    const third = lowerPass[i + 2];

    // Check for sequential letters (case-insensitive)
    if (/^[a-z]$/.test(first)) {
      const code1 = first.charCodeAt(0);
      const code2 = second.charCodeAt(0);
      const code3 = third.charCodeAt(0);

      // Check incremental (e.g., abc) or decremental (e.g., cba)
      if (
        (code2 === code1 + 1 && code3 === code1 + 2) ||
        (code2 === code1 - 1 && code3 === code1 - 2)
      ) {
        return true;
      }
    }

    // Check for sequential numbers (e.g., 123 or 321)
    if (/^\d$/.test(first)) {
      const num1 = Number.parseInt(first, 10);
      const num2 = Number.parseInt(second, 10);
      const num3 = Number.parseInt(third, 10);

      if (
        (num2 === num1 + 1 && num3 === num1 + 2) ||
        (num2 === num1 - 1 && num3 === num1 - 2)
      ) {
        return true;
      }
    }
  }
  return false;
}

const occupations = [
  "clinician",
  "dentist",
  "dietitian",
  "doctor",
  "nurse",
  "nutritionist",
  "phlebotomist",
];

// Validation rules for password
const passwordValidation = yup
  .string()
  .required("Password is required")
  .matches(/^\S*$/, "Password must not contain any spaces") // Correct no spaces check
  .matches(/^\S*$/, "Password must not contain any spaces") // Correct no spaces check
  .matches(
    /^(?!.*(\w)\1{2,})/,
    "Password must not contain three or more repetitive characters"
  )
  .test(
    "no-sequential",
    "Password should not contain sequences of three or more consecutive letters or numbers",
    (value) => !hasInvalidSequence(value)
  )
  .matches(
    /^(?=.*[A-Z])/,
    "Password must contain at least one uppercase letter"
  )
  .matches(
    /^(?=.*[a-z])/,
    "Password must contain at least one lowercase letter"
  )
  .matches(
    /^(?=.*[@$!%*?&])/,
    "Password must contain at least one special character"
  )
  .matches(/^(?=.*\d)/, "Password must contain at least one number")
  .min(8, "Minimum 8 characters");

// Schema for login validation
export const loginSchema = yup.object().shape({
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email"),
  password: yup.string().required("Password is required"),
});

export const vhLoginSchema = yup.object().shape({
  email: yup
    .string()
    .required("Please enter a valid email address.")
    .email("Please enter a valid email address.")
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,7}$/,
      "Please enter a valid email address."
    ),
  password: yup.string().required("Please enter a valid password."),
});

// Schema for forgot password validation
export const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .required("Email is required")
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, "Please enter a valid email")
    .email("Please enter a valid email"),
});

export const vhForgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .required("Email is required.")
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,7}$/,
      "Please enter a valid email address."
    )
    .email("Please enter a valid email address."),
});

// Schema for reset password validation
export const resetPasswordSchema = yup.object().shape({
  password: passwordValidation,
  confirmpassword: yup
    .string()
    .oneOf(
      [yup.ref("password"), null],
      "New password and Confirm password must match"
    )
    .oneOf(
      [yup.ref("password"), null],
      "New password and Confirm password must match"
    )
    .required("Confirm Password is required"),
});

// Schema for activation validation
export const activationSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .matches(/^\S*$/, "Password must not contain any spaces")
    .matches(/^(?=.*[A-Z])/, "One uppercase character")
    .matches(/^(?=.*[a-z])/, "One lowercase character")
    .matches(/^(?=.*[@$!%*?&])/, "One special character")
    .matches(/^(?=.*\d)/, "One number")
    .matches(
      /^(?!.*(\w)\1{2,})/,
      "Password must not contain three or more repetitive characters"
    )
    .test(
      "no-sequential",
      'Password contains sequential characters (e.g., "abc" or "123")',
      (value) => !hasInvalidSequence(value)
    )
    .min(8, "Minimum 8 characters"),
  occupation: yup.string().required("Please ensure you enter your occupation"),
  mobile: yup
    .string()
    .matches(/^\S*$/, "Mobile number must not contain any spaces")
    .matches(/^\+?\d+$/, "Mobile number must contain only digits")
    .min(10, "Mobile number must be at least 10 characters")
    .max(13, "Mobile number must not exceed 13 characters"),
  first_name: yup
    .string()
    .required("Please ensure you enter at least your first name"),
  last_name: yup
    .string()
    .required("Please ensure you enter at least your last name"),
});

// Schema for enabling multi-factor authentication
export const enableMfaSchema = yup.object().shape({
  otp: yup.number().required("Please ensure you enter your auth code"),
});

// Schema for account management validation
export const accountSchema = yup.object().shape({
  first_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "First name cannot contain numbers, spaces, or special characters"
    )
    .matches(/^[^\s]+$/, "First name cannot contain spaces")
    .required("Please ensure you enter at least your first name"),
  last_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "Last name cannot contain numbers, spaces, or special characters"
    )
    .matches(/^[^\s]+$/, "Last name cannot contain spaces")
    .required("Please ensure you enter at least your last name"),
  occupation: yup
    .string()
    .oneOf(occupations, "Please select one of the occupation options")
    .matches(/^[^\s]+$/, "Occupation cannot contain spaces")
    .required("Please select your occupation"),
  phone_number_primary: yup
    .string()
    .notRequired()
    .matches(/^\S*$/, "Mobile number must not contain any spaces")
    .matches(/^\+?\d+$/, "Mobile number must contain only digits")
    .min(10, "Mobile number must be at least 10 characters")
    .max(13, "Mobile number must not exceed 13 characters"),
});

export const vhAccountSchema = yup.object().shape({
  first_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "First name cannot contain numbers, spaces, or special characters."
    )
    .matches(/^[^\s]+$/, "First name cannot contain spaces.")
    .required("Please ensure you enter at least your first name."),
  last_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "Last name cannot contain numbers, spaces, or special characters."
    )
    .matches(/^[^\s]+$/, "Last name cannot contain spaces.")
    .required("Please ensure you enter at least your last name."),
  phone_number_primary: yup
    .string()
    .nullable()
    .matches(/^\S*$/, "Mobile number must not contain any spaces.")
    .matches(/^\+?\d+$/, "Mobile number must contain only digits.")
    .min(10, "Mobile number must be at least 10 characters.")
    .max(13, "Mobile number must not exceed 13 characters."),
});

// Schema for changing passwords
export const changePasswordSchema = yup.object().shape({
  current_password: yup.string().required("Current password is required"),
  new_password: passwordValidation
    .required("New password is required")
    .notOneOf(
      [yup.ref("current_password"), null],
      "New password cannot be the same as the current password"
    ),
  confirm_password: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("new_password"), null], "Passwords must match"),
});

export const vhChangePasswordSchema = yup.object().shape({
  current_password: yup.string().required("Current password is required."),
  new_password: passwordValidation
    .required("New password is required.")
    .matches(/^\S*$/, "Password must not contain any spaces")
    .matches(/^(?=.*[A-Z])/, "One uppercase character")
    .matches(/^(?=.*[a-z])/, "One lowercase character")
    .matches(/^(?=.*[@$!%*?&])/, "One special character")
    .matches(/^(?=.*\d)/, "One number")
    .matches(
      /^(?!.*(\w)\1{2,})/,
      "Password must not contain three or more repetitive characters"
    )
    .test(
      "no-sequential",
      'Password contains sequential characters (e.g., "abc" or "123")',
      (value) => !hasInvalidSequence(value)
    )
    .min(8, "Minimum 8 characters")
    .notOneOf(
      [yup.ref("current_password"), null],
      "New password cannot be the same as the current password."
    ),
  confirm_password: yup
    .string()
    .required("Confirm password is required.")
    .oneOf([yup.ref("new_password"), null], "Passwords must match."),
});

// Schema for entering barcode
export const enterBarcodeSchema = yup.object().shape({
  primary_barcode: yup.string().required("Primary barcode is required"),
  sample_collection_date: yup
    .string()
    .required("Sample collection date is required"),
  sample_collection_time: yup
    .string()
    .matches(/^\d{2}:\d{2}$/, "Time must be in HH:mm format")
    .test(
      "is-within-range",
      "Please enter a blood collection time within 30 minutes of the current time.",
      timeWithin30Min
    )
    .required("Sample collection time is required"),
  number_of_attempt: yup.number().required("Number of attempts is required"),
});

// Schema for clinical barcode in VH
export const vhClinicalBarcodeSchema = yup.object().shape({
  primary_barcode: yup.string().required("Primary barcode is required"),
  number_of_attempt: yup
    .mixed()
    .required("Please select number of draw attempts")
    .test(
      "is-attempt-selected",
      "Please select number of draw attempts",
      (val) => val === 1 || val === 2 || val === "1" || val === "2"
    ),
  sample_collection_date: yup
    .string()
    .required("Please enter sample collection date")
    .test("is-valid-date", "Please enter sample collection date", (val) => {
      if (!val) return false;
      const parts = val.split("-");
      if (parts.length !== 3) return false;
      const [year, month, day] = parts.map(Number);
      if (!year || !month || !day) return false;
      if (month < 1 || month > 12 || day < 1 || day > 31) return false;
      const d = new Date(year, month - 1, day);
      return (
        d.getFullYear() === year &&
        d.getMonth() === month - 1 &&
        d.getDate() === day
      );
    }),
  sample_collection_time: yup
    .string()
    .required("Please enter sample collection time")
    .test("is-valid-time", "Please enter sample collection time", (val) => {
      if (!val) return false;
      const parts = val.split(":");
      if (parts.length !== 2) return false;
      const h = Number(parts[0]);
      const m = Number(parts[1]);
      if (isNaN(h) || isNaN(m)) return false;
      return h >= 0 && h <= 23 && m >= 0 && m <= 59;
    })
    .test(
      "is-within-range",
      "Please enter a blood collection time within 30 minutes of the current time.",
      timeWithin30Min
    ),
});

// Schema for validating dates
export const dateValidationSchema = yup.object().shape({
  dob: yup
    .date()
    .test("valid-date", "Invalid date format", (value) => {
      if (!value) return true;
      const year = new Date(value).getFullYear();
      return year >= 1000 && year <= 9999;
    })
    .test("is-18-or-older", "You must be at least 18 years old", (value) => {
      if (!value) return true;

      // Calculate tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Calculate date 18 years before tomorrow
      const eighteenYearsAgo = new Date(tomorrow);
      eighteenYearsAgo.setFullYear(tomorrow.getFullYear() - 18);

      // The selected date must be on or before the date 18 years ago
      return new Date(value) <= eighteenYearsAgo;
    }),
  date_of_birth: yup
    .date()
    .test("valid-date", "Invalid date format", (value) => {
      if (!value) return true;
      const year = new Date(value).getFullYear();
      return year >= 1000 && year <= 9999;
    })
    .test("is-18-or-older", "You must be at least 18 years old", (value) => {
      if (!value) return true;

      // Calculate tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Calculate date 18 years before tomorrow
      const eighteenYearsAgo = new Date(tomorrow);
      eighteenYearsAgo.setFullYear(tomorrow.getFullYear() - 18);

      // The selected date must be on or before the date 18 years ago
      return new Date(value) <= eighteenYearsAgo;
    }),
});

// Schema for barcode rejction flow
export const rejectValidationSchema = yup.object().shape({
  notes: yup.string().required("Notes are required"),
  rejection_reason: yup.string().required("Reason for rejection is required"),
});

export const clinicalDetailsNotFoundSchema = yup.object().shape({
  first_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "Last name cannot contain numbers, spaces, or special characters"
    )
    .matches(/^[^\s]+$/, "Last name cannot contain spaces")
    .required("First name is required"),

  last_name: yup
    .string()
    .matches(
      /^[a-zA-Z]+$/,
      "Last name cannot contain numbers, spaces, or special characters"
    )
    .matches(/^[^\s]+$/, "Last name cannot contain spaces")
    .required("Last name is required"),

  date_of_birth: yup
    .date()
    .required("Date of birth is required")
    .test("age", "You must be at least 18 years old", (value) => {
      return (
        value && new Date().getFullYear() - new Date(value).getFullYear() >= 18
      );
    }),

  sex: yup.string().required("Sex at birth is required"),

  patient_id: yup.string().required("Patient ID is required"),

  crio_identifier: yup.string().notRequired(),
});
